import React, { useEffect, useRef, useState } from "react"
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  styled,
  useTheme,
  Avatar,
  AvatarGroup,
  Tabs,
  Tab,
  Box,
  List,
  ListItem
} from "@mui/material"
import CommonModal from "components/CommonModal"
import AppPagination from "components/StyledComponents/AppPagination"
import styles from "./notifications.module.scss"
import { formatListWithTooltip, formatTime, toTitleCase } from "helpers/functions"
import TableNoRecords from "components/TableNoRecords"
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward"
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward"
import { useDispatch, useSelector } from "react-redux"
import { appActions } from "redux/app/slice/app"
import AppButton from "components/StyledComponents/AppButton"
import DeleteConfirmationDialog from "components/DeleteConfirmationDialog"
import { resendNotificationAction } from "redux/DMV/actions/user"
import ProfileDisplayer from "components/ProfileDisplayer"

const CustomTabs = styled(Tabs)(({ theme }) => ({
  position: "sticky",
  top: 45,
  zIndex: 10,
  background: `${theme.palette.background.default} !important`,
  "& .MuiTabs-indicator": {
    backgroundColor: theme.palette.aqua.main
  },
  "& .MuiTab-root.Mui-selected": {
    color: theme.palette.aqua.main
  },
  "& .MuiTab-root": {
    textTransform: "none",
    padding: "20px 28px 20px 28px"
  },
  "& .MuiTabs-scroller ": {
    marginLeft: "12px"
  }
}))
const PushNotificationListTable = ({
  columns = [],
  rows = [],
  setPage,
  currentPage,
  totalPage
}) => {
  const [sort, setSort] = useState("")
  const [sortDirection, setSortDirection] = useState("")
  const dispatch = useDispatch()
  const rowId = useRef(null)
  const theme = useTheme()
  const [resendNotification, setResendNotification] = useState(false)
  const [openUserPopup, setOpenUserPopup] = useState(false)
  const [selectedNotification, setSelectedNotification] = useState(null)
  const [activeTab, setActiveTab] = useState(0)
  const { isLoading } = useSelector((state) => state?.app)

  const handleSortDirection = (dir, id) => {
    setSortDirection(dir)
    handleSort(dir, id)
  }

  const handleSort = (dir, id) => {
    if (dir === "asc") {
      setSort(id)
      dispatch(appActions.setOrderBy(id))
    } else {
      setSort(`-${id}`)
      dispatch(appActions.setOrderBy(`-${id}`))
    }
  }

  useEffect(() => {
    dispatch(appActions.setOrderBy("title"))
  }, [])

  function handleResend(id) {
    rowId.current = id
    setResendNotification(true)
  }

  function handleSendNotification() {
    const payload = {
      notification: rowId.current
    }
    dispatch(resendNotificationAction(payload))
    rowId.current = null
  }
  const handleOpenUserPopup = (row) => {
    setSelectedNotification(row)
    setOpenUserPopup(true)
    setActiveTab(0)
  }

  const handleCloseUserPopup = () => {
    setOpenUserPopup(false)
    setSelectedNotification(null)
  }

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
  }

  function getGroupedUsers(users) {
    if (!users) return {}
    return users.reduce((acc, user) => {
      const restaurantName = user.restaurant || "-"
      if (!acc[restaurantName]) {
        acc[restaurantName] = []
      }
      acc[restaurantName].push(user)
      return acc
    }, {})
  }

  return (
    <>
      <DeleteConfirmationDialog
        title={`Are you sure you want to resend this notification?`}
        description="  "
        style={{ textAlign: "center", width: "80%" }}
        onClose={() => setResendNotification(false)}
        onDelete={() => handleSendNotification()}
        open={resendNotification}
        btnText="Resend"
        type="warning"
      />
      <Paper>
        <TableContainer sx={{ maxHeight: 700 }}>
          <CustomTable stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    style={{ minWidth: column.minWidth }}>
                    <Typography variant="body2Regular" style={{ opacity: 0.6 }}>
                      {column.label}{" "}
                      {column?.isSorting && (
                        <span style={{ cursor: "pointer" }}>
                          {sortDirection === "desc" && sort === `-${column?.sortKey}` ? (
                            <ArrowDownwardIcon
                              onClick={() => handleSortDirection("asc", column?.sortKey)}
                              fontSize="small"></ArrowDownwardIcon>
                          ) : (
                            <ArrowUpwardIcon
                              onClick={() => handleSortDirection("desc", column?.sortKey)}
                              fontSize="small"></ArrowUpwardIcon>
                          )}
                        </span>
                      )}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length ? (
                rows?.map((row) => {
                  return (
                    <TableRow key={row.id}>
                      <TableCell>
                        <div className="d-flex flex-column">
                          <Typography variant="body1Regular">{row?.title ?? "-"}</Typography>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1Regular">
                          {toTitleCase(row?.content ?? "-") ?? "-"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body1Regular">
                          {row?.employee_job_class_names?.length
                            ? formatListWithTooltip(row?.employee_job_class_names, theme)
                            : "-"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {formatListWithTooltip(row?.restaurant_names, theme)}
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body1Regular">
                          {formatTime(row?.scheduled_date, row?.scheduled_time) || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography
                          variant="body1Regular"
                          className={
                            row?.status?.toLowerCase() === "delivered"
                              ? styles.deliver
                              : row?.status === "failed"
                              ? styles.failure
                              : styles.schedule
                          }>
                          {toTitleCase(row?.status) || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box
                          sx={{ cursor: "pointer" }}
                          className="d-flex flex-column align-items-center"
                          onClick={() => handleOpenUserPopup(row)}>
                          <AvatarGroup
                            max={3}
                            variant="circular"
                            sx={{
                              "& .MuiAvatarGroup-avatar": {
                                fontSize: 11
                              }
                            }}>
                            {row?.users?.map((user, idx) => (
                              <Avatar
                                key={idx}
                                alt={user?.name}
                                src={user?.image}
                                sx={{ width: 40, height: 40 }}
                              />
                            ))}
                          </AvatarGroup>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <AppButton
                          onClick={() => handleResend(row?.id)}
                          sx={{ padding: "10px 20px" }}
                          disabled={row?.status?.toLowerCase() === "scheduled" ? true : false}>
                          Resend
                        </AppButton>
                      </TableCell>
                    </TableRow>
                  )
                })
              ) : (
                <TableNoRecords
                  colSpan={8}
                  label={
                    isLoading
                      ? "Please wait we are getting the notifications..."
                      : "No New Notification"
                  }
                />
              )}
            </TableBody>
          </CustomTable>
        </TableContainer>
      </Paper>
      <CommonModal
        open={openUserPopup}
        onClose={handleCloseUserPopup}
        maxWidth="sm"
        hideAction={true}
        sx={{ backgroundColor: theme.palette.background.default, pt: 0 }}>
        <Box>
          <Typography variant="h6" textAlign="center" className={styles.header}>
            Notification Status
          </Typography>
          {selectedNotification?.users?.length > 0 ? (
            <>
              <CustomTabs
                value={activeTab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto">
                {Object.keys(getGroupedUsers(selectedNotification?.users)).map(
                  (restaurant, idx) => (
                    <Tab key={idx} label={restaurant} />
                  )
                )}
              </CustomTabs>
              <Box mt={2}>
                {Object.values(getGroupedUsers(selectedNotification?.users))[activeTab]?.map(
                  (user, idx) => (
                    <List key={idx}>
                      <ListItem sx={{ py: 1.5, px: 2 }}>
                        <Box className="d-flex align-items-center gap-2 " sx={{ flex: 1 }}>
                          <ProfileDisplayer image={user?.image} name={user?.name} />
                          <Typography variant="body1Regular">{user?.name}</Typography>
                        </Box>

                        <Typography
                          variant="body1Regular"
                          sx={{
                            color: user?.opened ? theme.palette.success.main : "inherit",
                            opacity: user?.opened ? 1 : 0.6
                          }}>
                          {user?.opened ? "Opened" : "Not Opened"}
                        </Typography>
                      </ListItem>
                    </List>
                  )
                )}
              </Box>
            </>
          ) : (
            <Typography sx={{ textAlign: "center", py: 3 }}>No users found</Typography>
          )}
        </Box>
      </CommonModal>
      {rows?.length ? (
        <AppPagination onPageChange={setPage} totalPages={totalPage} currentPage={currentPage} />
      ) : (
        ""
      )}
    </>
  )
}

export default PushNotificationListTable

const CustomTable = styled(Table)(({ theme }) => ({
  backgroundColor: theme.palette.background.main,
  borderCollapse: "collapse !important",
  "& .MuiTableCell-root": {
    padding: "12px 24px 12px 0px",
    backgroundColor: theme.palette.background.main,
    borderBottom: "none",
    boxSizing: "border-box",
    "&:last-child": {
      paddingRight: 0
    }
  },
  "& .MuiTableRow-root": {
    borderBottom: `2px solid #272a2c`,
    "&:last-child": {
      borderBottom: "none"
    }
  },
  "& .MuiTableCell-head": {
    verticalAlign: "bottom",
    position: "sticky",
    borderBottom: `2px solid #272a2c`,
    "&:last-child": {
      paddingRight: 0
    }
  }
}))
